import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PublicHeader, Wordmark } from "../components/PublicHeader";
import { GoogleButton } from "../components/GoogleButton";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const inputCls = "mt-1.5 w-full rounded-lg border border-[#e3e0d3] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#94a01a]";
const labelCls = "text-xs uppercase tracking-[0.15em] font-medium text-[#51637a]";

export default function EmployerSignUp() {
  const [form, setForm] = useState({ full_name: "", company_name: "", email: "", password: "", phone: "", job_title: "", consent: false });
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please accept the consent checkbox");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register/employer", form);
      setUser(data);
      toast.success("Employer account created");
      navigate("/employer");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f3ee]">
      <PublicHeader />
      <div className="pt-16 min-h-screen flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-lg bg-white rounded-lg border border-[#e3e0d3] p-8 sm:p-10">
          <Wordmark />
          <h1 className="text-2xl font-bold text-[#0d2f4d] tracking-tight mt-6">Create an employer account</h1>
          <p className="text-sm text-[#51637a] mt-1">Set up your company and submit your first role.</p>
          <div className="mt-6">
            <GoogleButton roleHint="employer" label="Sign up with Google" testId="employer-google-signup" />
          </div>
          <div className="mt-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-[#e3e0d3]" />
            <span className="text-xs text-[#51637a] uppercase tracking-[0.15em]">or with email</span>
            <div className="h-px flex-1 bg-[#e3e0d3]" />
          </div>
          <form onSubmit={submit} className="mt-6 space-y-5">
            <div>
              <label className={labelCls}>Full name</label>
              <input required value={form.full_name} onChange={set("full_name")} data-testid="employer-signup-fullname" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Company name</label>
              <input required value={form.company_name} onChange={set("company_name")} data-testid="employer-signup-company" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Work email</label>
              <input type="email" required value={form.email} onChange={set("email")} data-testid="employer-signup-email" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Password (min 8 characters)</label>
              <input type="password" required minLength={8} value={form.password} onChange={set("password")} data-testid="employer-signup-password" className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Phone number</label>
                <input type="tel" required value={form.phone} onChange={set("phone")} data-testid="employer-signup-phone" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Your job title</label>
                <input required value={form.job_title} onChange={set("job_title")} data-testid="employer-signup-jobtitle" className={inputCls} />
              </div>
            </div>
            <label className="flex items-start gap-3 text-sm text-[#51637a] cursor-pointer">
              <input type="checkbox" checked={form.consent} onChange={set("consent")} data-testid="employer-signup-consent"
                className="mt-0.5 h-4 w-4 accent-[#C1CC24]" />
              I consent to The Ditch Careers storing my company and contact details to support our hiring needs.
            </label>
            <button type="submit" disabled={busy} data-testid="employer-signup-submit"
              className="w-full rounded-full bg-[#0d2f4d] text-white font-medium py-3.5 hover:bg-[#1c4d78] transition-colors disabled:opacity-60">
              {busy ? "Creating account..." : "Create employer account"}
            </button>
          </form>
          <p className="mt-6 text-sm text-[#51637a]">Already have an account? <Link to="/signin" className="text-[#7C8514] font-medium hover:underline" data-testid="employer-signup-signin-link">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
